import { useCallback, useMemo, useState } from 'react';
import useStoreAuth from 'src/hooks/useStoreAuth';

import { readTaxModuleEnabled } from '@hooks/_taxModule';
import useDebounce from '@hooks/useDebounce';
import { ME_QUERY_KEY, useMe, useUsers } from '@hooks/users';
import { useSearchParams } from '@hooks/useSearchParams';
import useStoreAppConfig from '@hooks/useStoreAppConfig';
import type {
  CreateUsersUsersRequestDataAttributesProfile,
  HotelChainRead,
} from '@spazious/client-backoffice';
import {
  BackofficeGetHotelChainsQueryHookOptions,
  useGetHotelChains,
} from '@spazious/react-backoffice';
import handleRequestError from '@utils/handleRequestError';
import { legacyHookOptions } from '@utils/legacyHookOptions';

const SPAZIOUS_ADMIN_ROLE = 1;

const ME_PROFILE_INCLUDE = [
  'profile.hotelChain',
  'profile.hotel',
  'profile.cluster',
  'profile.country',
  'profile.profileMarket',
];

export type HotelChainOption = {
  value: string;
  label: string;
  slug?: string;
  orderPrefix?: string;
  taxModuleEnabled: boolean;
};

type MeRelation = { id: string; name?: string } | null;
type MeProfile = {
  role?: number | null;
  scopes?: string[];
  gender?: string | null;
  phone?: string | null;
  postalCode?: string | null;
  address?: string | null;
  hotel?: MeRelation;
  country?: MeRelation;
  cluster?: MeRelation;
  profileMarket?: MeRelation;
  hotelChain?: MeRelation;
};
type Me = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  profile?: MeProfile;
};

/** Shared data hook for the header hotel-chain single-select (desktop + mobile). */
export function useHotelChainSelector() {
  const [auth] = useStoreAuth();
  const isSpaziousAdmin = auth?.role === SPAZIOUS_ADMIN_ROLE;

  const [{ hotelChain }, { setHotelChain, resetSelectedHotels }] = useStoreAppConfig();
  const [, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [searchChain, setSearchChain] = useState<string | undefined>(undefined);
  const debounceSearchChain = useDebounce(
    (value: string | undefined) => setSearchChain(value),
    500
  );
  const [inputValue, setInputValue] = useState('');
  const [accumulatedChains, setAccumulatedChains] = useState<
    (
      | { id: string; attributes?: { name?: string; slug?: string; orderPrefix?: string } }
      | HotelChainRead
    )[]
  >([]);

  const { normalizedData: meData } = useMe({
    include: ME_PROFILE_INCLUDE,
    enabled: isSpaziousAdmin,
  });
  const me = meData as unknown as Me | null;

  // Only the `updateUser` mutation is used here; the selector never reads the
  // users list. Disable the list query so non-admins (who lack the read-users
  // scope) don't fire a `users/` GET that 403s. Refetching `me` after the switch
  // is what refills the new chain's hotels in the store (`QuickUserToggler`).
  const { updateUser } = useUsers({
    enabled: false,
    mutationKey: [[ME_QUERY_KEY]],
    onError: handleRequestError,
  });

  const chainsQueryResult = useGetHotelChains(
    legacyHookOptions<BackofficeGetHotelChainsQueryHookOptions>({
      variables: { pageNumber: page, filters: { search: searchChain } },
      refetchOnWindowFocus: false,
      skip: !isSpaziousAdmin,
    })
  );

  const chainOptions: HotelChainOption[] = useMemo(
    () =>
      [...accumulatedChains, ...(chainsQueryResult?.data?.data ?? [])].map(
        ({ attributes, id }) => ({
          value: id,
          label: attributes?.name ?? '',
          slug: attributes?.slug,
          orderPrefix: attributes?.orderPrefix ?? undefined,
          taxModuleEnabled: readTaxModuleEnabled(attributes),
        })
      ),
    [accumulatedChains, chainsQueryResult?.data?.data]
  );

  const handleChange = useCallback(
    async (option: HotelChainOption) => {
      const previous = hotelChain;
      setHotelChain({
        hotelChainId: option.value,
        name: option.label,
        slug: option.slug ?? '',
        orderPrefix: option.orderPrefix ?? '',
        hotels: [],
        taxModuleEnabled: option.taxModuleEnabled,
      });

      const dropPreviousHotels = () => {
        resetSelectedHotels();
        setSearchParams({ hotels: undefined });
      };

      if (!me?.id) {
        dropPreviousHotels();
        return;
      }
      const profile = me.profile ?? {};
      await updateUser(
        {
          userId: me.id,
          email: me.email,
          profile: {
            role: profile.role,
            scopes: profile.scopes,
            gender: profile.gender ?? null,
            phone: profile.phone || null,
            postalCode: profile.postalCode || null,
            address: profile.address || null,
            profileMarketId: profile.profileMarket?.id ?? null,
            hotelChainId: option.value,
            hotelId: profile.hotel?.id ?? null,
            countryId: profile.country?.id ?? null,
            clusterId: profile.cluster?.id ?? null,
          } as unknown as CreateUsersUsersRequestDataAttributesProfile,
        },
        {
          onSuccess: dropPreviousHotels,
          onError: () => {
            if (previous) setHotelChain(previous);
          },
        }
      );
    },
    [hotelChain, me, resetSelectedHotels, setHotelChain, setSearchParams, updateUser]
  );

  const handleInputChange = useCallback(
    (value: string | undefined) => {
      setAccumulatedChains([]);
      setPage(1);
      debounceSearchChain(value || undefined);
    },
    [debounceSearchChain]
  );

  const handleLoadMore = useCallback(() => {
    setAccumulatedChains((prev) => [...prev, ...(chainsQueryResult?.data?.data ?? [])]);
    setPage((prev) => prev + 1);
  }, [chainsQueryResult?.data?.data]);

  const meta = (
    chainsQueryResult?.data as { meta?: { pagination?: { pages?: number } } } | undefined
  )?.meta;
  const shouldLoadMore = (meta?.pagination?.pages ?? 0) > page;

  const value: HotelChainOption | null = useMemo(() => {
    if (!hotelChain?.hotelChainId) return null;
    return {
      value: hotelChain.hotelChainId,
      label: hotelChain.name ?? '',
      slug: hotelChain.slug,
      orderPrefix: hotelChain.orderPrefix,
      taxModuleEnabled: hotelChain.taxModuleEnabled ?? false,
    };
  }, [hotelChain]);

  const orderedChainOptions: HotelChainOption[] = useMemo(() => {
    if (!value?.value) return chainOptions;
    const withoutSelected = chainOptions.filter((option) => option.value !== value.value);
    const selectedInList = withoutSelected.length !== chainOptions.length;
    if (selectedInList || !searchChain) return [value, ...withoutSelected];
    return chainOptions;
  }, [chainOptions, value, searchChain]);

  return {
    isSpaziousAdmin,
    chainOptions: orderedChainOptions,
    value,
    inputValue,
    setInputValue,
    handleChange,
    handleInputChange,
    handleLoadMore,
    shouldLoadMore,
    isLoading: chainsQueryResult?.loading,
  };
}
